import React, {
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Col } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  fetchAllAdministrationRules,
  fetchFilterSettings,
  fetchTableSettings,
  updateFilterSettings,
  updateTableSettings,
} from '../../actions/administration';
import { localStorageConstants, locale } from '../../constants/globals';
import { userActions } from '../../constants/user-actions';
import AuthContext from '../../context/auth_context';
import { useModalToggle } from '../../hooks/useModalToggle';
import withTranslations from '../../utils/HighOrderComponent';
import { checkHasTableMetaData } from '../AgGrid/action-utils';
import CustomButton from '../Reusable/CustomButton';
import CustomModal from '../Reusable/Modal/CustomModal';
import EditSettingsFormContainer from './EditSettingsFormContainer';
import LanguageSelect from './LanguageSelect';
import { AdministrationForms } from './utils';

const ModalDataWrapper = (props) => {
  const {
      t,
      type,
      filterButtonVisible,
      tableButtonVisible,
      attributeStates,
      refresh = () => {},
    } = props || {},
    { Modals, Form } = t || {};
  const { translations } = useSelector((state) => state.translationsReducer);
  const { AgGrid } = translations || {};
  const { Header } = AgGrid || {};
  const { Buttons } = Form || {};

  const dispatch = useDispatch();
  const history = useHistory();
  const submitRef = useRef();

  const currentPath = history.location.pathname;
  const typeFromPath = currentPath.replace(/\//g, '');

  const { allowedActions } = useContext(AuthContext);

  const { settings, loadingSettings, administrationRulesList } = useSelector(
    (state) => state.administrationReducer
  );

  const [currentLanguage, setCurrentLanguage] = useState(
    localStorage.getItem(localStorageConstants.LOCALE)?.toUpperCase() ||
      locale.default.toUpperCase()
  );

  const [hideSaveButton, setHideSaveButton] = useState(false);

  const [showModal, toggleModal, item, toggleUpdate, reference] =
    useModalToggle(false);

  useEffect(() => {
    allowedActions?.includes(userActions.ADMINISTRATION_FIND_ALL) &&
      dispatch(fetchAllAdministrationRules());
  }, [dispatch, allowedActions]);

  useEffect(() => {
    if (item) {
      if (
        reference === AdministrationForms?.FILTERS ||
        reference === AdministrationForms?.META_DATA_FILTERS
      ) {
        dispatch(fetchFilterSettings(item?.key, currentLanguage));
      }
      if (reference === AdministrationForms?.TABLE) {
        dispatch(fetchTableSettings(item?.key, currentLanguage));
      }
      if (reference === AdministrationForms?.ACTIONS) {
        dispatch(fetchTableSettings(item?.key, currentLanguage));
      }
    }
  }, [item, dispatch, reference, currentLanguage]);

  const modalCloseHandler = () => {
    toggleModal(false);
  };

  const handleModalOpen = (ruleSettingsType) => {
    const typeOfSettings = type || typeFromPath;
    const pageItem = administrationRulesList.find(
      (item) => item.key === typeOfSettings
    );
    toggleUpdate(
      { label: pageItem?.label, key: pageItem?.key },
      ruleSettingsType
    );
  };

  const submitCallback = () => {
    modalCloseHandler();
    refresh();
  };

  const onSubmitHandler = (settingsPayload) => {
    const action =
      reference === AdministrationForms?.FILTERS ||
      reference === AdministrationForms.META_DATA_FILTERS
        ? updateFilterSettings
        : updateTableSettings;

    dispatch(
      action(
        settingsPayload,
        Form.NotificationMessages,
        submitCallback,
        item?.label,
        currentLanguage
      )
    );
  };

  const modalTitle = useMemo(() => {
    const {
      modalForTableActionsSettingsTitle,
      modalForFilterSettingsTitle,
      modalForTableSettingsTitle,
    } = Modals || {};
    let firstPartOfTitle = '';
    switch (reference) {
      case AdministrationForms.META_DATA_FILTERS:
      case AdministrationForms.FILTERS:
        firstPartOfTitle = modalForFilterSettingsTitle;
        break;
      case AdministrationForms?.ACTIONS:
        firstPartOfTitle = modalForTableActionsSettingsTitle;
        break;
      default:
        firstPartOfTitle = modalForTableSettingsTitle;
        break;
    }
    return `${firstPartOfTitle}: ${item?.label}`;
  }, [reference, item, Modals]);

  return (
    <Fragment>
      {filterButtonVisible && type && (
        <Col className="d-flex align-items-center justify-content-end">
          <CustomButton
            className="default-btn-primary ml-1"
            onClick={() => handleModalOpen(AdministrationForms?.FILTERS)}
            label={Buttons?.changeFilter}
            startIcon={<i className="fa-solid fa-magnifying-glass" />}
            disabled={attributeStates?.length !== 0}
            type="button"
            size="small"
            tooltipText={
              attributeStates?.length !== 0
                ? Header?.changeFilterTooltipDisabled
                : Header?.changeFilterTooltip
            }
          />
          <CustomButton
            className="default-btn-primary ml-1"
            onClick={() =>
              handleModalOpen(AdministrationForms?.META_DATA_FILTERS)
            }
            label={Buttons?.metaDataFilter}
            startIcon={<i className="fa-solid fa-magnifying-glass" />}
            disabled={attributeStates?.length !== 0}
            type="button"
            hidden={!checkHasTableMetaData(type)}
            size="small"
            tooltipText={
              attributeStates?.length !== 0
                ? Header?.changeFilterTooltipDisabled
                : Header?.changeFilterTooltip
            }
          />
        </Col>
      )}
      {tableButtonVisible && type && (
        <CustomButton
          className="default-btn-primary"
          onClick={() => handleModalOpen(AdministrationForms?.TABLE)}
          label={Buttons?.changeTable}
          startIcon={<i className="fa-solid fa-table" />}
          type="button"
          size="small"
          tooltipText={Header?.changeTableTooltip}
        />
      )}
      {showModal && (
        <CustomModal
          show={showModal}
          hideModal={modalCloseHandler}
          title={modalTitle}
          hideSaveButton={hideSaveButton}
          isView={hideSaveButton}
          modalSize={'xl'}
          saveButtonClassName={'admin-btn-save ml-330 h-36'}
          submitRef={submitRef}
          loading={loadingSettings}
          footerContent={
            <div className={'position-absolute start-0 px-4'}>
              <LanguageSelect
                currentLanguage={currentLanguage}
                setCurrentLanguage={setCurrentLanguage}
              />
            </div>
          }
        >
          <EditSettingsFormContainer
            settings={settings}
            onSubmit={onSubmitHandler}
            reference={reference}
            hideSaveButton={hideSaveButton}
            setHideSaveButton={setHideSaveButton}
            formType={AdministrationForms}
            submitRef={submitRef}
            type={type}
            translations={Form}
            itemForUpdate={item}
            modalCloseHandler={modalCloseHandler}
          />
        </CustomModal>
      )}
    </Fragment>
  );
};

export default withTranslations(ModalDataWrapper, 'AdministrationPage');
